import { useCompanyReadScope } from "contexts/CompanyReadScope";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useApolloClient } from "@apollo/client";
import { Select } from "@outpave/ds";
import type { SelectOption } from "@outpave/ds";
import {
  allocationOption,
  fetchAllocationPage,
} from "utils/allocations/allocationOptions";
import { AllocationData } from "../admin/allocations/AllocationTable";
import type {
  AllocationNode,
  AllocationType,
} from "utils/allocations/allocationOptions";
import { useSelectSearch } from "hooks/useSelectSearch";
import { useBrowseAllAllocations } from "hooks/useBrowseAllAllocations";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";
import { useTranslation } from "react-i18next";
import AllocationDrawer from "components/admin/allocations/AllocationDrawer";

export interface AllocationIdAndTypeProps {
  allocationId: string;
  allocationCode?: string;
  allocationType: {
    key: string;
    name: string;
  };
}

/** What a pick reports, which is all any caller keeps. */
export type SelectedAllocation = Pick<
  AllocationNode,
  "id" | "allocationCode" | "allocationType"
>;

export interface MinimalAllocationData {
  id: string;
  allocationCode: string;
}

interface AllocationSearchAutoCompleteProps {
  onSelectAllocation: (selected: SelectedAllocation | null) => void;
  allocation: Partial<AllocationData> | null;
  title?: string | null;
  errorText?: string;
  forceLoading?: boolean;
  required?: boolean;
  /** Formik's `handleBlur`, to mark the field touched. */
  handleBlur?: React.FocusEventHandler<HTMLInputElement>;
  allocationType?: AllocationType;
  filterByAllocationType?: boolean;
  additionalVariables?: { [key: string]: any };
  disabled?: boolean;
  filterDescendantsOfParent?: AllocationIdAndTypeProps | null;
  /** Offers everything, hidden allocations included: for the admin page and reports, never for coding spend. */
  showAll?: boolean;
}

/**
 * The allocation picker. It opens on the user's recent picks and searches the
 * server for the rest, through `useSelectSearch`. A child's caption is its
 * parent path and a top-level allocation's is its type; search results keep
 * their tree level as `depth`, which the DS draws as the indent and elbow.
 *
 * `filterDescendantsOfParent` still prunes each page as it arrives: an
 * allocation cannot be reparented under its own descendant.
 */
const AllocationSearchAutoComplete: React.FC<
  AllocationSearchAutoCompleteProps
> = ({
  allocation,
  onSelectAllocation,
  title = "Allocation",
  errorText = "",
  required = false,
  handleBlur = () => {},
  allocationType,
  filterByAllocationType = false,
  additionalVariables = {},
  disabled = false,
  filterDescendantsOfParent = null,
  showAll = false,
}) => {
  const selectedCompanyId = useCompanyReadScope();
  const client = useApolloClient();
  const { browseAll, browseAction } = useBrowseAllAllocations();
  const seenRef = useRef(new Map<string, AllocationData>());
  const [descendantCache] = useState(new Map<string, boolean>());
  const [openCreateAllocationDrawer, setOpenCreateAllocationDrawer] =
    useState(false);
  useEffect(() => {
    descendantCache.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDescendantsOfParent?.allocationId]);

  const selectedAllocation = useMemo(() => {
    if (!allocation || !allocation?.id || !allocation.allocationCode) {
      return null;
    }
    if (!allocationType) return null;
    return { ...allocation, allocationType } as AllocationData;
  }, [allocation, allocationType]);

  const isDescendant = useCallback(
    (
      parent: AllocationIdAndTypeProps,
      child: AllocationData,
      all: AllocationData[],
    ): boolean => {
      const cacheKey = `${parent.allocationId}-${child.id}`;
      const cached = descendantCache.get(cacheKey);
      if (cached !== undefined) return cached;
      if (child.parentAllocationId == null) {
        descendantCache.set(cacheKey, false);
        return false;
      }
      if (child.parentAllocationId === parent.allocationId) {
        descendantCache.set(cacheKey, true);
        return true;
      }
      const parentOfChild = all.find((a) => a.id === child.parentAllocationId);
      const result = parentOfChild
        ? isDescendant(parent, parentOfChild, all)
        : false;
      descendantCache.set(cacheKey, result);
      return result;
    },
    [descendantCache],
  );

  const fetchPage = useCallback(
    async (query: string, after: string | null) => {
      const page = await fetchAllocationPage(client, {
        query,
        after,
        allocationType:
          allocationType && filterByAllocationType ? allocationType : null,
        showAll,
        browseAll: browseAll || !!selectedCompanyId,
        variables: { ...additionalVariables, selectedCompanyId },
      });
      const all: AllocationData[] = page.nodes;

      const visible = filterDescendantsOfParent
        ? all.filter((a) => {
            if (
              a.id === filterDescendantsOfParent.allocationId ||
              filterDescendantsOfParent.allocationId === null ||
              a.allocationType?.key !==
                filterDescendantsOfParent.allocationType?.key
            ) {
              return false;
            }
            return !isDescendant(filterDescendantsOfParent, a, all);
          })
        : all;

      visible.forEach((a) => seenRef.current.set(a.id, a));
      return {
        options: visible.map((a) => allocationOption(a, page.recent)),
        hasNextPage: page.hasNextPage,
        endCursor: page.endCursor,
      };
    },
    // The query inputs plus the prune; identities are covered by the members read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      client,
      selectedCompanyId,
      allocationType,
      filterByAllocationType,
      additionalVariables,
      filterDescendantsOfParent,
      isDescendant,
      showAll,
      browseAll,
    ],
  );

  const seed = useMemo<SelectOption | null>(
    () =>
      selectedAllocation
        ? {
            id: selectedAllocation.id,
            label: selectedAllocation.allocationCode,
            caption: selectedAllocation.allocationType?.name ?? undefined,
            depth: selectedAllocation.level ?? 0,
          }
        : null,
    [selectedAllocation],
  );

  const search = useSelectSearch<SelectOption>({
    fetchPage,
    seed,
    refetchKey: String(browseAll),
  });
  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const { t } = useTranslation();

  return (
    <>
      <Select
        label={title ?? undefined}
        aria-label={title ? undefined : t("Allocation")}
        onBlur={handleBlur}
        searchable
        required={required}
        disabled={disabled}
        errorText={errorText || undefined}
        options={search.options}
        loading={search.loading}
        highlight={search.query}
        onInputChange={search.onInputChange}
        onScrollEnd={search.onScrollEnd}
        noOptionsText={
          search.query || browseAll || showAll
            ? t("No allocations found")
            : t("NoRecentAllocations")
        }
        // Pickers that already list everything have nothing to switch to.
        bottomAction={showAll ? undefined : browseAction(search.query)}
        value={selectedAllocation?.id ?? null}
        topAction={
          isAdminOrHigher && !selectedCompanyId
            ? {
                label: t("Create New Allocation"),
                onClick: () => {
                  setOpenCreateAllocationDrawer(true);
                },
              }
            : undefined
        }
        onChange={(id) => {
          if (!id) {
            search.reset();
            onSelectAllocation(null);
            return;
          }
          // `seenRef` holds only what a page returned, and the seeded
          // selection is not one of those, so it resolves from the seed.
          onSelectAllocation(
            seenRef.current.get(id) ??
              (selectedAllocation?.id === id ? selectedAllocation : null),
          );
        }}
      />
      <AllocationDrawer
        open={openCreateAllocationDrawer}
        handleCloseDrawer={() => setOpenCreateAllocationDrawer(false)}
        afterAllocationSubmit={(created) => {
          setOpenCreateAllocationDrawer(false);
          // A parent picker leaves it to the user: the new one may sit under the allocation being edited.
          if (filterDescendantsOfParent) return;
          onSelectAllocation({
            id: created.allocationId,
            allocationCode: created.allocationCode ?? "",
            allocationType: created.allocationType,
          });
        }}
      />
    </>
  );
};

export default AllocationSearchAutoComplete;
